import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowRight, Play, Calendar as CalIcon, ChevronRight, Heart, Users, BookOpen, Globe } from 'lucide-react';
import { news } from '@/data/site';
import { useSiteData } from '@/context/SiteDataContext';
import api from '@/lib/api';
import EditText from '@/components/editable/EditText';
import EditImage from '@/components/editable/EditImage';
import EditLink from '@/components/editable/EditLink';
import SectionToggle, { useSectionHidden } from '@/components/editable/SectionToggle';
import PillarItems from '@/components/editable/PillarItems';
import GalleryRows from '@/components/GalleryRows';
import { useEditMode } from '@/context/EditModeContext';
import AdminHint from '@/components/editable/AdminHint';

export default function Home() {
  const { departments, timeline, settings, pageContent, loadPageContent } = useSiteData();
  const { editMode } = useEditMode();
  const [apiEvents, setApiEvents] = useState([]);
  useEffect(() => {
    api.get('/events').then(r => setApiEvents(r.data || [])).catch(() => {});
  }, []);
  const featured = apiEvents.filter(e => e.featured).slice(0, 3);
  const deptList = (departments || []).slice(0, 10);
  const tlList = timeline || [];
  const s = settings || {};
  const c = pageContent?.home || {};
  const hideWelcome = useSectionHidden('home', 'welcome');
  const hidePillars = useSectionHidden('home', 'pillars');
  const hideEvents = useSectionHidden('home', 'events');
  const hideDepts = useSectionHidden('home', 'depts');
  const hideTimeline = useSectionHidden('home', 'timeline');
  const hideNews = useSectionHidden('home', 'news');
  const hideCta = useSectionHidden('home', 'cta');
  useEffect(() => { if (!pageContent?.home && loadPageContent) loadPageContent('home'); }, [pageContent, loadPageContent]);

  return (
    <div className="relative">
      {/* Fixed full-page video background */}
      <EditImage field="hero_video" target="settings" category="hero" accept="video/*,image/*" wrapperClass="fixed inset-0 -z-10">
        <div className="fixed inset-0 -z-10" data-testid="home-video-bg">
          {s.hero_video ? (
            <video autoPlay muted loop playsInline poster={s.hero_image} className="w-full h-full object-cover">
              <source src={s.hero_video} type="video/mp4" />
            </video>
          ) : (
            <img src={s.hero_image || 'https://images.pexels.com/photos/34328510/pexels-photo-34328510.jpeg?w=2400'} alt="Worship service" className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-royal-dark/30" />
        </div>
      </EditImage>

      {/* HERO — video shows fully through */}
      <section data-testid="home-hero" className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: s.hero_overlay_color
              || 'linear-gradient(to bottom, rgba(31,15,53,0.6), rgba(31,15,53,0.3), rgba(31,15,53,0.7))'
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 w-full">
          <div className="max-w-3xl fade-up">
            <EditText field="hero_overline" target="page:home" as="div" className="overline text-gold mb-6">
              {c.hero_overline || s.site_subtitle || 'Church of God in Christ'}
            </EditText>
            <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] font-bold tracking-tight drop-shadow-lg">
              <EditText field="hero_title_main" target="settings" as="span">
                {s.hero_title_main || 'Wisconsin First'}
              </EditText>
              <br />
              <EditText field="hero_title_accent" target="settings" as="span" className="text-gold">
                {s.hero_title_accent || 'Ecclesiastical Jurisdiction'}
              </EditText>
            </h1>
            <EditText field="tagline" target="settings" as="p" multiline className="mt-8 text-white/90 text-lg lg:text-xl max-w-2xl leading-relaxed drop-shadow">
              {s.tagline || 'Serving Wisconsin through Worship, Evangelism, Leadership, and Community.'}
            </EditText>
            <div className="mt-10 flex flex-wrap gap-4">
              <EditLink field="hero_cta1_href" target="settings" currentHref={s.hero_cta1_href || '/about'}>
                <HeroCta to={s.hero_cta1_href || '/about'} testid="hero-learn-more" variant="gold">
                  <EditText field="hero_cta1_label" target="settings" as="span">{s.hero_cta1_label || 'Learn More'}</EditText>
                  <ArrowRight className="w-4 h-4" />
                </HeroCta>
              </EditLink>
              <EditLink field="hero_cta2_href" target="settings" currentHref={s.hero_cta2_href || '/events'}>
                <HeroCta to={s.hero_cta2_href || '/events'} testid="hero-events-btn" variant="outline">
                  <CalIcon className="w-4 h-4" />
                  <EditText field="hero_cta2_label" target="settings" as="span">{s.hero_cta2_label || 'Upcoming Events'}</EditText>
                </HeroCta>
              </EditLink>
              <EditLink field="hero_cta3_href" target="settings" currentHref={s.hero_cta3_href || '/media'}>
                <HeroCta to={s.hero_cta3_href || '/media'} testid="hero-watch-live-btn" variant="outline">
                  <Play className="w-4 h-4" />
                  <EditText field="hero_cta3_label" target="settings" as="span">{s.hero_cta3_label || 'Watch Live'}</EditText>
                </HeroCta>
              </EditLink>
            </div>
          </div>
        </div>
      </section>

      {!hideWelcome && (
      <section className="relative bg-white/85 backdrop-blur-md py-20 lg:py-28">
        <div className="absolute top-4 right-4 z-30"><SectionToggle pageSlug="home" sectionKey="welcome" label="Prelate's Welcome" /></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <EditText field="welcome_overline" target="page:home" as="div" className="overline mb-4">
              {c.welcome_overline || 'A Message From the Prelate'}
            </EditText>
            <EditText field="welcome_heading" target="page:home" as="h2" className="font-serif text-3xl lg:text-5xl text-royal font-bold leading-tight">
              {c.welcome_heading || 'A House of Worship. A People of Purpose.'}
            </EditText>
          </div>
          <div className="md:col-span-7 text-charcoal-muted text-lg leading-relaxed space-y-4">
            <EditText field="welcome_p1" target="page:home" as="p" multiline>
              {c.welcome_p1 || `Welcome to the digital home of the ${s.site_name || 'Wisconsin First Ecclesiastical Jurisdiction'} — a fellowship of churches united across the state under the banner of the Church of God in Christ.`}
            </EditText>
            <EditText field="welcome_p2" target="page:home" as="p" multiline>
              {c.welcome_p2 || 'We are called to advance the Kingdom of God through anointed preaching, uncompromising holiness, and love in action. Whether you are a member, a visitor, or someone seeking Christ — we welcome you.'}
            </EditText>
            <div className="flex gap-6 pt-4">
              <div>
                <EditText field="stat_churches" target="settings" as="div" className="font-serif text-4xl text-royal font-bold">{s.stat_churches || '40+'}</EditText>
                <div className="text-sm text-charcoal-muted">Local Churches</div>
              </div>
              <div>
                <EditText field="stat_departments" target="settings" as="div" className="font-serif text-4xl text-royal font-bold">{s.stat_departments || '15'}</EditText>
                <div className="text-sm text-charcoal-muted">Departments</div>
              </div>
              <div>
                <EditText field="stat_years" target="settings" as="div" className="font-serif text-4xl text-royal font-bold">{s.stat_years || '48'}</EditText>
                <div className="text-sm text-charcoal-muted">Years Serving</div>
              </div>
            </div>
          </div>
        </div>
      </section>)}

      {!hidePillars && (
      <section className="relative bg-[#F8F9FA]/80 backdrop-blur-md py-20 lg:py-28">
        <div className="absolute top-4 right-4 z-30"><SectionToggle pageSlug="home" sectionKey="pillars" label="Four Pillars" /></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <EditText field="pillars_overline" target="page:home" as="div" className="overline mb-3">{c.pillars_overline || 'Our Four Pillars'}</EditText>
            <EditText field="pillars_heading" target="page:home" as="h2" className="font-serif text-3xl lg:text-5xl text-royal font-bold gold-underline">
              {c.pillars_heading || 'Called. Consecrated. Commissioned.'}
            </EditText>
          </div>
          <PillarItems items={c.pillars_items} />
        </div>
      </section>)}

      {!hideEvents && (featured.length > 0 || editMode) && (
      <section className="relative bg-white/85 backdrop-blur-md py-20 lg:py-28">
        <div className="absolute top-4 right-4 z-30"><SectionToggle pageSlug="home" sectionKey="events" label="Featured Events" /></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <EditText field="events_overline" target="page:home" as="div" className="overline mb-3">{c.events_overline || 'On the Calendar'}</EditText>
              <EditText field="events_heading" target="page:home" as="h2" className="font-serif text-3xl lg:text-5xl text-royal font-bold gold-underline">{c.events_heading || 'Featured Events'}</EditText>
            </div>
            <Link to="/events" className="text-royal hover:text-gold flex items-center gap-1 font-semibold">View All Events <ChevronRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {featured.length === 0 && (
              <AdminHint className="md:col-span-3 text-center text-charcoal-muted italic bg-royal/5 rounded-lg p-8">
                This section is hidden from visitors until an event is marked "Feature on the homepage" (edit any event on the Events page).
              </AdminHint>
            )}
            {featured.map((e) => (
              <Link key={e.id} to={`/events`} data-testid={`featured-event-${e.id}`} className="card-tactile overflow-hidden group block">
                <div className="aspect-[4/3] overflow-hidden">{e.image && <img src={e.image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                <div className="p-6">
                  <div className="text-xs text-gold font-semibold tracking-widest uppercase mb-2">{new Date(e.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                  <h3 className="font-serif text-xl text-royal font-bold mb-2 group-hover:text-gold transition">{e.title}</h3>
                  <p className="text-charcoal-muted text-sm mb-4">{e.location}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>)}

      {!hideDepts && (
      <section className="relative bg-royal-dark/85 backdrop-blur-md text-white py-20 lg:py-28">
        <div className="absolute top-4 right-4 z-30"><SectionToggle pageSlug="home" sectionKey="depts" label="Departments" /></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <EditText field="depts_overline" target="page:home" as="div" className="overline mb-3">{c.depts_overline || 'Ministry Departments'}</EditText>
            <EditText field="depts_heading" target="page:home" as="h2" className="font-serif text-3xl lg:text-5xl text-white font-bold">{c.depts_heading || 'Every Member. Every Ministry.'}</EditText>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {deptList.map((d) => (
              <Link key={d.id} to={`/departments/${d.id}`} data-testid={`dept-card-${d.id}`} className="group relative aspect-square rounded-xl overflow-hidden">
                <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-royal-dark via-royal-dark/60 to-transparent" />
                <div className="absolute inset-0 p-4 flex items-end">
                  <div className="font-serif text-white text-sm lg:text-base font-bold leading-tight">{d.name}</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center"><Link to="/departments" className="btn-gold">View All Departments <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </section>)}

      {!hideTimeline && (
      <section className="relative bg-white/85 backdrop-blur-md py-20 lg:py-28">
        <div className="absolute top-4 right-4 z-30"><SectionToggle pageSlug="home" sectionKey="timeline" label="Heritage Timeline" /></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <EditText field="timeline_overline" target="page:home" as="div" className="overline mb-3">{c.timeline_overline || 'Our Heritage'}</EditText>
            <EditText field="timeline_heading" target="page:home" as="h2" className="font-serif text-3xl lg:text-5xl text-royal font-bold gold-underline">{c.timeline_heading || 'A Legacy of Faith'}</EditText>
          </div>
          <div className="relative border-l-2 border-gold/40 ml-4 md:mx-auto md:max-w-3xl space-y-10">
            {tlList.map((t) => (
              <div key={t.id} className="relative pl-8 md:pl-12">
                <div className="absolute left-0 -ml-[9px] w-4 h-4 rounded-full bg-gold border-4 border-white ring-2 ring-gold" />
                <div className="font-serif text-royal text-2xl font-bold">{t.year} · {t.title}</div>
                <div className="text-charcoal-muted mt-2">{t.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>)}

      {!hideNews && (
      <section className="relative bg-[#F8F9FA]/80 backdrop-blur-md py-20 lg:py-28">
        <div className="absolute top-4 right-4 z-30"><SectionToggle pageSlug="home" sectionKey="news" label="News & Announcements" /></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <EditText field="news_overline" target="page:home" as="div" className="overline mb-3">{c.news_overline || 'Latest From the Jurisdiction'}</EditText>
              <EditText field="news_heading" target="page:home" as="h2" className="font-serif text-3xl lg:text-5xl text-royal font-bold gold-underline">{c.news_heading || 'News & Announcements'}</EditText>
            </div>
            <Link to="/news" className="text-royal hover:text-gold flex items-center gap-1 font-semibold">All News <ChevronRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {news.map((n) => (
              <Link key={n.id} to={`/news/${n.id}`} className="card-tactile overflow-hidden group">
                <div className="aspect-[16/10] overflow-hidden"><img src={n.image} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-6">
                  <div className="text-xs text-gold uppercase tracking-widest font-semibold">{n.category}</div>
                  <h3 className="font-serif text-xl text-royal font-bold mt-2 mb-3 group-hover:text-gold transition">{n.title}</h3>
                  <p className="text-charcoal-muted text-sm">{n.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
          {s.facebook_page_url && (
            <div className="mt-10 text-center">
              <a href={s.facebook_page_url} target="_blank" rel="noopener noreferrer"
                 data-testid="home-facebook-link"
                 className="inline-flex items-center gap-2 text-royal hover:text-gold font-semibold">
                Follow us on Facebook for daily updates <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </section>)}

      {!hideCta && (
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute top-4 right-4 z-30"><SectionToggle pageSlug="home" sectionKey="cta" label="Giving CTA" /></div>
        <div className="absolute inset-0" style={{ backgroundColor: s.cta_bg_color || undefined }}>
          <img src={s.cta_bg_image || 'https://images.unsplash.com/photo-1600423809757-8cd9d249bd63?w=2400'} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ backgroundColor: s.cta_overlay_color || 'rgba(59,30,94,0.85)' }} />
        </div>
        <div className="relative max-w-4xl mx-auto text-center px-6 text-white">
          <EditText field="cta_overline" target="page:home" as="div" className="overline text-gold mb-4">{c.cta_overline || 'Advance the Kingdom'}</EditText>
          <EditText field="cta_heading" target="page:home" as="h2" className="font-serif text-3xl lg:text-5xl font-bold">{c.cta_heading || 'Your Generosity Fuels the Mission'}</EditText>
          <EditText field="cta_text" target="page:home" as="p" multiline className="mt-4 text-white/80 text-lg max-w-2xl mx-auto">{c.cta_text || 'Give securely online through Givelify to support the ministry, missions, and outreach of the jurisdiction.'}</EditText>
          <div className="mt-8 flex gap-4 justify-center flex-wrap">
            <Link to="/giving" data-testid="cta-give-btn" className="btn-gold">Give Now <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/prayer" className="btn-outline-white">Request Prayer</Link>
          </div>
        </div>
      </section>)}

      <GalleryRows scope="page:home-bottom" wrapClass="bg-[#F8F9FA] py-14" innerClass="max-w-7xl mx-auto px-6" />
    </div>
  );
}

// -----------------------------------------------------------------------------
// HeroCta — helper so href strings starting with http:// open externally and
// internal paths use react-router <Link>.
// -----------------------------------------------------------------------------
function HeroCta({ to, testid, variant, children }) {
  const cls = variant === 'gold' ? 'btn-gold' : 'btn-outline-white';
  const isExternal = /^https?:\/\//i.test(to || '');
  if (isExternal) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" data-testid={testid} className={cls}>
        {children}
      </a>
    );
  }
  return <Link to={to || '/'} data-testid={testid} className={cls}>{children}</Link>;
}
